import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarBlank, WarningCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { EmptyState, PageHeader } from "@/components/common";
import DateTimeLocalPicker, { parseDateTimeLocal } from "@/components/DateTimeLocalPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const DISPOSITIONS = ["NEW", "QUEUE", "NOANSWER", "BUSY", "FAILED", "DNC", "NI", "COMPLETED", "SALE"];

export default function AdminCallbackReconciliation() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resolveRow, setResolveRow] = useState(null);
  const [mode, setMode] = useState("schedule");
  const [scheduledAt, setScheduledAt] = useState("");
  const [disposition, setDisposition] = useState("NEW");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get("/admin/callback-reconciliation");
      setItems(data.items || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openResolve = (row) => {
    setResolveRow(row);
    setMode("schedule");
    setScheduledAt("");
    setDisposition("NEW");
  };

  const submitResolve = async () => {
    if (!resolveRow) return;
    setBusy(true);
    try {
      let body;
      if (mode === "schedule") {
        const due = parseDateTimeLocal(scheduledAt);
        if (!due) {
          toast.error("Choose a valid future due time");
          setBusy(false);
          return;
        }
        body = { scheduled_at: due.toISOString() };
      } else {
        body = { disposition };
      }
      await api.post(
        `/admin/callback-reconciliation/${resolveRow.lead_id}/resolve`,
        body,
        { params: { campaign_id: resolveRow.campaign_id } },
      );
      toast.success(mode === "schedule" ? "Callback scheduled" : `Disposition set to ${disposition}`);
      setResolveRow(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-callback-reconciliation">
      <PageHeader
        title="Callback reconciliation"
        description="PD CALLBACK leads blocked because the schedule row or next_attempt_at is missing. Never auto-dials — schedule a due time or set an authorized disposition."
        actions={(
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
        )}
      />

      {error ? (
        <p className="text-sm text-red-600" data-testid="callback-recon-error">{error}</p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CalendarBlank}
          title="No blocked callbacks"
          description="Hopper rows needing reconciliation will appear here."
        />
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead / phone</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Block reason</TableHead>
                <TableHead>next_attempt_at</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={`${row.campaign_id}:${row.lead_id}`} data-testid={`callback-recon-row-${row.lead_id}`}>
                  <TableCell>
                    <div className="font-medium text-sm">{row.customer_name || "—"}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">{row.phone || row.lead_id}</div>
                  </TableCell>
                  <TableCell className="text-sm">{row.campaign_name || row.campaign_id}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-amber-800 text-xs font-medium">
                      <WarningCircle size={14} />
                      {row.blocked_reason || "needs_reconciliation"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs tabular-nums text-muted-foreground">
                    {row.next_attempt_at || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{row.updated_at || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="secondary" onClick={() => openResolve(row)}>
                      Resolve
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!resolveRow} onOpenChange={(o) => !o && setResolveRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve blocked callback</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Lead <span className="font-mono text-foreground">{resolveRow?.lead_id}</span>
              {" · "}
              {resolveRow?.phone || "no phone"}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={mode === "schedule" ? "default" : "outline"}
                onClick={() => setMode("schedule")}
              >
                Schedule callback
              </Button>
              <Button
                size="sm"
                variant={mode === "disposition" ? "default" : "outline"}
                onClick={() => setMode("disposition")}
              >
                Set disposition
              </Button>
            </div>
            {mode === "schedule" ? (
              <div className="space-y-1">
                <Label>Due time (required — never invented)</Label>
                <DateTimeLocalPicker value={scheduledAt} onChange={setScheduledAt} />
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Disposition</Label>
                <Select value={disposition} onValueChange={setDisposition}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISPOSITIONS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveRow(null)}>Cancel</Button>
            <Button
              disabled={busy || (mode === "schedule" && !scheduledAt)}
              onClick={submitResolve}
              data-testid="callback-recon-submit"
            >
              {busy ? "Saving…" : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
